/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';
import { Link } from 'react-router';

import { Tag, Typography2 } from '@coursera/cds-core';
import { CheckIcon, WarningFilledIcon } from '@coursera/cds-icons';

import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import { AutoSaveContext } from 'bundles/grading/components/staff-graded-assignments/auto-save/utils';
import type { SGASubmitter } from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';
import { hasUnsubmittedChanges } from 'bundles/grading/utils/submissionUtils';

import _t from 'i18n!nls/grading';

type Props = {
  submitter: SGASubmitter;
  isSelectedSubmitter: boolean;
};

const styles = {
  container: () => css`
    display: block;
    width: 100%;
    height: 48px;
    margin-bottom: var(--cds-spacing-50);
    border-radius: 4px;
    box-sizing: border-box;
  `,
  link: () => css`
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: 0 var(--cds-spacing-150);
    text-decoration: none;
    color: var(--cds-color-neutral-primary);
    border: 1px solid transparent;
    outline: none;

    &:focus,
    &:hover,
    &:active {
      outline: none;
      text-decoration: none;
      color: var(--cds-color-interactive-primary);
      background-color: var(--cds-color-neutral-background-primary);
    }

    &:focus {
      color: var(--cds-color-interactive-primary);
      background-color: var(--cds-color-neutral-background-primary);
      border: 1px solid var(--cds-color-interactive-stroke-primary-focus);
      box-sizing: border-box;
      border-radius: 4px;
    }
  `,
  isSelected: () => css`
    color: var(--cds-color-interactive-primary);
    background-color: var(--cds-color-neutral-background-primary);
  `,
  submitterName: () => css`
    padding-right: var(--cds-spacing-100);
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    padding: 0 var(--cds-spacing-100) 0 var(--cds-spacing-150);
  `,
  gradedCheckIcon: () => css`
    flex: none;
  `,
  ungradedContainer: () => css`
    flex: none;
    width: 16px;
    height: 16px;
  `,
};

// Component for individual submitter
const Submitter: React.FC<Props> = ({ submitter, isSelectedSubmitter }) => {
  const { autoSaveTaskStates } = useContext(AutoSaveContext);
  const { getUrl } = useContext(SGAPageContext);

  const currentAutoSaveTask = autoSaveTaskStates[submitter.submitterId];
  const isSavingOrSubmitting = currentAutoSaveTask?.isSaving || currentAutoSaveTask?.isSubmitting;

  const hasSubmission = submitter.submittedAt !== null;
  const isLate = submitter?.submittedAt?.seconds
    ? parseInt(submitter.submittedAt.seconds, 10) > parseInt(submitter.submissionDeadline.seconds, 10)
    : false;

  const submitterHasUnsubmittedChanges =
    hasUnsubmittedChanges(submitter.submissionStatus, submitter.isSubmissionGraded) && !isSavingOrSubmitting;

  const titleCheckIcon = _t('Graded');
  const titleWarningIcon = _t('There are unsubmitted changes');

  return (
    <li css={styles.container}>
      <Link to={getUrl(submitter.id)} css={[styles.link, isSelectedSubmitter && styles.isSelected]}>
        {submitter.isSubmissionGraded && !submitterHasUnsubmittedChanges && (
          <CheckIcon
            size="small"
            color="success"
            aria-label={titleCheckIcon}
            title={titleCheckIcon}
            css={styles.gradedCheckIcon}
          />
        )}
        {submitterHasUnsubmittedChanges && (
          <WarningFilledIcon size="small" aria-label={titleWarningIcon} title={titleWarningIcon} />
        )}
        {!submitter.isSubmissionGraded && !submitterHasUnsubmittedChanges && <span css={styles.ungradedContainer} />}
        <Typography2
          className="pii-hide"
          variant="bodyPrimary"
          color="inherit"
          css={styles.submitterName}
          component="span"
        >
          {submitter.name}
        </Typography2>
        {hasSubmission && isLate && (
          <Tag variant="status" priority="secondary" color="default">
            {_t('Late')}
          </Tag>
        )}
      </Link>
    </li>
  );
};

export default Submitter;
